import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/http";
import { toast } from "sonner";
import { BellRing, ArrowUpRight, BellOff, ChevronDown } from "lucide-react";

const CYAN = "#3D5A45", AMBER = "#B5704F", ROSE = "#A94442";
const sevColor = (s) => (s === "high" ? ROSE : s === "medium" ? AMBER : CYAN);

export const AlertsPanel = () => {
  const [data, setData] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const load = () => api.get("/intelligence/alerts").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const snooze = async (key) => {
    try { await api.post("/intelligence/alerts/snooze", { key, days: 7 }); toast.success("Snoozed for 7 days"); load(); }
    catch { toast.error("Couldn't snooze"); }
  };
  const unsnooze = async (key) => { try { await api.delete(`/intelligence/alerts/snooze/${key}`); load(); } catch { toast.error("Couldn't restore"); } };
  if (!data) return null;
  return (
    <div className="rounded-2xl border bg-card shadow-sm p-4 mb-6" data-testid="intel-alerts">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BellRing className="h-4 w-4" style={{ color: data.counts.high ? ROSE : CYAN }} />
          <span className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Alerts · grouped, one digest a day at {String(data.digest_hour).padStart(2, "0")}:00</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          {data.counts.high > 0 && <span className="rounded-full px-2 py-0.5" style={{ color: ROSE, border: `1px solid ${ROSE}55` }} data-testid="alerts-high-count">{data.counts.high} urgent</span>}
          {data.counts.medium > 0 && <span className="rounded-full px-2 py-0.5" style={{ color: AMBER, border: `1px solid ${AMBER}55` }} data-testid="alerts-medium-count">{data.counts.medium} watch</span>}
          {data.last_digest && <span>Last digest {data.last_digest.date?.split("-").reverse().join("/")}</span>}
        </div>
      </div>
      {data.alerts.length === 0 && <div className="text-sm text-muted-foreground" data-testid="alerts-empty">All quiet — nothing has crossed a threshold today.</div>}
      <div className="grid md:grid-cols-2 gap-3">
        {data.groups.map((g) => (
          <div key={g.group} className="rounded-xl border p-3" data-testid={`alert-group-${g.group}`}>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">{g.label}</div>
            <div className="space-y-2">
              {g.alerts.map((a) => (
                <div key={a.key} className="text-sm" data-testid={`alert-${a.key}`}>
                  <div className="flex items-start gap-2">
                    <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ background: sevColor(a.severity), boxShadow: `0 0 8px ${sevColor(a.severity)}` }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium leading-snug">{a.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{a.detail}</div>
                      <div className="flex items-center gap-3 mt-1.5 text-[11px]">
                        <button onClick={() => setOpenKey(openKey === a.key ? null : a.key)} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid={`alert-why-${a.key}`}>Why? <ChevronDown className={`h-3 w-3 transition-transform ${openKey === a.key ? "rotate-180" : ""}`} /></button>
                        <Link to={a.link} className="inline-flex items-center gap-1 hover:underline" style={{ color: CYAN }} data-testid={`alert-link-${a.key}`}>Act on it <ArrowUpRight className="h-3 w-3" /></Link>
                        <button onClick={() => snooze(a.key)} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 ml-auto" data-testid={`alert-snooze-${a.key}`}><BellOff className="h-3 w-3" />Snooze 7d</button>
                      </div>
                      {openKey === a.key && <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed" data-testid={`alert-why-text-${a.key}`}>{a.why}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {data.snoozed?.length > 0 && (
        <div className="mt-3 text-[11px] text-muted-foreground flex flex-wrap gap-2 items-center" data-testid="alerts-snoozed">
          <span>Snoozed:</span>
          {data.snoozed.map((a) => <button key={a.key} onClick={() => unsnooze(a.key)} className="rounded-full border border-border px-2 py-0.5 hover:border-white/40" title="Click to restore" data-testid={`alert-unsnooze-${a.key}`}>{a.title} · until {a.snoozed_until?.split("-").reverse().join("/")}</button>)}
        </div>
      )}
    </div>
  );
};
